import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  TextArea,
  Badge,
  DotLoading,
  Grid,
  Popup,
  SearchBar,
  Divider,
  List,
  AutoCenter,
} from 'antd-mobile';
import { SendOutlined } from '@ant-design/icons';
import { LeftOutline } from 'antd-mobile-icons';
import { CarOutlined } from '@ant-design/icons';

const CarListPopup = ({ carList, carListPopup, setCarListPopup }) => {
  const [filteredCarList, setFilteredCarList] = useState(carList);
  const [searchCarListKeyword, setSearchCarListKeyword] = useState('');
  useEffect(() => {
    if (searchCarListKeyword.trim().length > 0) {
      setFilteredCarList(
        carList.filter((car) =>
          car.toLowerCase().includes(searchCarListKeyword.toLowerCase())
        )
      );
    } else {
      setFilteredCarList(carList);
    }
  }, [carList, searchCarListKeyword]);
  return (
    <Popup
      visible={carListPopup}
      onMaskClick={() => {
        setCarListPopup(false);
      }}
      position="right"
      bodyStyle={{
        width: '60vw',
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
        backgroundColor: '#eef0f3',
        overflowY: 'auto',
      }}>
      <div className="car-list" style={{ padding: '16px' }}>
        <SearchBar
          className="custom-search-bar"
          placeholder="搜尋"
          onChange={(value) => {
            setSearchCarListKeyword(value);
          }}
          style={{
            '--border-radius': '8px',
            '--background': '#ffffff',
            '--height': '48px',
            '--padding-left': '12px',
            borderColor: 'darkgray',
          }}
        />
        <Divider style={{ borderColor: 'darkgray' }}>
          共 {carList.length} 台
        </Divider>
        <List mode="card" style={{ margin: '0' }}>
          {filteredCarList.map((value, index) => (
            <List.Item key={value}>
              <CarOutlined /> <span>{value}</span>
            </List.Item>
          ))}
        </List>
      </div>
    </Popup>
  );
};

const ChatRoom = ({
  friend,
  endChat,
  sendMessage,
  isReadFriend,
  isLoadingMessages,
  getPreviousMessages,
  oldScrollHeight,
  setOldScrollHeight,
  isForceFriend,
  incomingMsgFrom,
  setIncomingMsgFrom,
}) => {
  const [carListPopup, setCarListPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [newMsgAlert, setNewMsgAlert] = useState(false);
  const [scrollPosition, setScrollPosition] = useState('bottom');
  const msgDiv = useRef(null);

  let messageList = [];

  // 初始化時，將訊息清單依照時間做一次排序
  if (friend.user_id) {
    const msgList = [...friend.messages];
    msgList.sort((a, b) => a.send_time.localeCompare(b.send_time));
    messageList = msgList;
  }

  // 在線、離線
  const IsOnlineBadge = () => {
    const color = friend.is_online ? '#87D068' : '#EEEEEE';
    return (
      <Badge
        color={color}
        content={Badge.dot}
        style={{ margin: '5px', marginRight: '10px' }}
      />
    );
  };

  const convertCustomDate = (originalDateString) => {
    // 輸入範例："2024-04-03"
    // 輸出範例："04/03(三)"
    if (!originalDateString) {
      return '';
    }
    const originalDate = new Date(originalDateString.replace(/-/g, '/'));

    const formattedDate = originalDate
      .toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\//g, '/');

    const days = ['日', '一', '二', '三', '四', '五', '六'];
    const dayOfWeek = days[originalDate.getDay()];
    return `${formattedDate}(${dayOfWeek})`;
  };

  const convertCustomTime = (datatimeStr) => {
    if (!datatimeStr) {
      return '';
    }
    const originalDate = new Date(datatimeStr);
    const taiwanDate = new Date(originalDate.getTime() + 8 * 3600000);
    return taiwanDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 送出訊息
  const send = () => {
    sendMessage(message);
    setMessage('');
  };

  // 結束聊天
  const endCurrentChat = () => {
    setScrollPosition('bottom');
    endChat(friend.user_id);
  };

  // 控制訊息串畫面顯示位置
  const scrollToAppropriateHeight = (targetPosition = null) => {
    if (msgDiv.current) {
      const { scrollHeight, clientHeight } = msgDiv.current;
      const bottom = scrollHeight - clientHeight;

      // 如果有傳入目標位置
      if (targetPosition) {
        if (targetPosition === 'bottom') {
          msgDiv.current.scrollTop = bottom; // 滑到最下方
        } else if (scrollPosition === 'top') {
          msgDiv.current.scrollTop = 0; // 滑到上方
        }

        // 如果沒傳入目標位置
      } else if (scrollPosition === 'bottom') {
        // 滑到最下方
        msgDiv.current.scrollTop = bottom;
      } else if (scrollPosition === 'top') {
        // 保持目前視野位置，不隨著訊息串更新而亂跳
        const scrollOffset = scrollHeight - oldScrollHeight;
        msgDiv.current.scrollTop = scrollOffset;
      }
    }
  };

  // 滾輪監聽
  const onScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = msgDiv.current;
    const isAtTop = msgDiv.current.scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight + 1 > scrollHeight;

    if (isAtTop && !isLoadingMessages && friend.user_id) {
      getPreviousMessages(friend.user_id);
      setOldScrollHeight(scrollHeight);
      setScrollPosition('top');
    } else if (isAtBottom) {
      setIncomingMsgFrom('');
      setNewMsgAlert(false);
      setScrollPosition('bottom');
    } else {
      setScrollPosition('middle');
    }
  };

  useEffect(() => {
    if (friend.user_id) {
      if (friend.unread_count > 0) {
        isReadFriend(friend.user_id); // 開始聊天後，直接已讀對方
      }
    }

    scrollToAppropriateHeight();

    const divRef = msgDiv.current;
    divRef.addEventListener('scroll', onScroll);

    if (incomingMsgFrom === friend.user_id && scrollPosition !== 'bottom') {
      setNewMsgAlert(true);
    }

    return () => divRef.removeEventListener('scroll', onScroll);
  });

  return (
    <div
      className="chat-box-container"
      key={friend.user_id ? friend.user_id : '1234567890'}>
      <div className="chat-top-bar text-body font-semibold">
        <Grid columns={4} gap={8} style={{ width: '100%' }}>
          <Grid.Item span={1}>
            <div
              className="go-back-btn"
              onClick={endCurrentChat}
              hidden={isForceFriend}>
              <LeftOutline />
              上一頁
            </div>
          </Grid.Item>
          <Grid.Item span={2}>
            <div>
              <IsOnlineBadge />
              {friend.name}
            </div>
          </Grid.Item>
          <Grid.Item span={1}>
            <div className="car-list-btn" onClick={() => setCarListPopup(true)}>
              <CarOutlined
                style={{ fontSize: '18px', color: 'white' }}></CarOutlined>
            </div>
          </Grid.Item>
        </Grid>
      </div>
      <CarListPopup
        carList={friend.carno ? friend.carno : []}
        carListPopup={carListPopup}
        setCarListPopup={setCarListPopup}
      />
      <div className="chat-middle-part" ref={msgDiv}>
        <div className="loading-message">
          {isLoadingMessages ? (
            <div>
              載入更多訊息
              <DotLoading />
            </div>
          ) : (
            <div style={{ opacity: 0 }}>留白空間</div>
          )}
        </div>
        {messageList.map((msg, index, arr) => {
          let isFirstOfTheDay = false;
          if (index === 0) {
            isFirstOfTheDay = true;
          } else {
            const previous = arr[index - 1];
            if (previous.send_date !== msg.send_date) {
              isFirstOfTheDay = true;
            }
          }

          const isMine = msg.sender !== friend.user_id;
          if (isMine) {
            return (
              <div key={msg.send_date + msg.send_time} className="w-full">
                <AutoCenter>
                  <div
                    className="chat-date text-sm font-normal"
                    hidden={!isFirstOfTheDay}>
                    {convertCustomDate(msg.send_date)}
                  </div>
                </AutoCenter>
                <div key={msg.send_time} className="outgoing">
                  <div className="text-xs text-right font-semibold grey">
                    {msg.is_read ? '已讀' : ''}
                    <br></br>
                    {convertCustomTime(msg.send_time)}
                  </div>
                  <div className="pink-bubble text-body font-normal">
                    {msg.message}
                  </div>
                </div>
              </div>
            );
          } else {
            return (
              <div key={msg.send_date + msg.send_time} className="w-full">
                <AutoCenter>
                  <div
                    className="chat-date text-sm font-normal"
                    hidden={!isFirstOfTheDay}>
                    {convertCustomDate(msg.send_date)}
                  </div>
                </AutoCenter>
                <div key={msg.send_time} className="incoming">
                  <div className="white-bubble text-body font-normal">
                    {msg.message}
                  </div>
                  <div className="text-xs font-semibold grey">
                    {convertCustomTime(msg.send_time)}
                  </div>
                </div>
              </div>
            );
          }
        })}
      </div>
      <div className="bottom-bar">
        <div className="new-msg">
          {newMsgAlert ? (
            <span
              className="alert"
              onClick={() => {
                scrollToAppropriateHeight('bottom');
              }}>
              您有新訊息 !
            </span>
          ) : (
            <></>
          )}
        </div>
        <TextArea
          className="message-textarea"
          type="text"
          placeholder="輸入訊息..."
          value={message}
          onChange={(value) => setMessage(value)}
        />
        <Button className="send-button" onClick={send}>
          <SendOutlined />
        </Button>
      </div>
    </div>
  );
};

export default ChatRoom;
